import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraEmptyState, SpecraEyebrow, SpecraIconButton, SpecraSurface, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Info } from 'lucide-react';
import { cmDateShort } from '../Delivery/Deliverables/lib/format';
// SAD-1005: the Today band, the page's only big number. The verdict counts
// everything waiting on the viewer against the target "nothing overdue";
// beside it, the next 5 working days name the viewer's own dated items.
// It is not the Command Center's heat calendar: these are mine, by name.
/** Agenda items a day shows before "+N more". */ const PER_DAY = 2;
export const WAITING_RULE = 'Yours: late and at-risk deliverables of teams you own (or of programs you lead, where the team has no owner), filings waiting on approval and requests on programs you lead, corrections and issues assigned to you, and items shared with you. Overdue means past its due date.';
const weekday = (iso)=>new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'short',
        timeZone: 'UTC'
    });
export function TodayBand({ verdict, agenda, today, loading }) {
    if (!loading && verdict.total === 0) {
        return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            "data-testid": Testids.dashboard.nothingWaiting,
            framed: true,
            size: "compact",
            title: "Your deliverables, approvals and requests show up here."
        });
    }
    const tone = loading ? 'muted' : verdict.tone === 'danger' ? 'danger' : 'success';
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        className: "grid overflow-hidden border md:grid-cols-[var(--component-verdict-width)_minmax(0,1fr)]",
        "data-testid": Testids.dashboard.today,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-[var(--space-2)] bg-[var(--surface-rail)] p-[var(--space-5)]",
                "data-testid": Testids.dashboard.verdict,
                "data-tone": loading ? 'loading' : verdict.tone,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-[var(--space-1)]",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                children: "Waiting on you"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTooltip, {
                                className: "max-w-72",
                                content: WAITING_RULE,
                                delayDuration: 150,
                                children: /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    label: "What counts as yours",
                                    size: "sm",
                                    variant: "ghost",
                                    children: /*#__PURE__*/ _jsx(Info, {})
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-baseline gap-[var(--space-2)]",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                className: "font-display text-[length:var(--fs-ui-verdict)] leading-none",
                                tone: tone,
                                children: loading ? '…' : verdict.total.toLocaleString()
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "body-sm",
                                tone: "muted",
                                children: "target: nothing overdue"
                            })
                        ]
                    }),
                    loading ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        children: "Loading…"
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "meta",
                                tone: "muted",
                                children: [
                                    verdict.overdue > 0 ? `${verdict.overdue.toLocaleString()} overdue` : 'none overdue',
                                    ' · ',
                                    verdict.dueSoon.toLocaleString(),
                                    " due by ",
                                    weekday(verdict.lastDay),
                                    ' ',
                                    cmDateShort(verdict.lastDay)
                                ]
                            }),
                            verdict.sentence ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                "data-testid": Testids.dashboard.verdictSentence,
                                size: "body-ui",
                                children: verdict.sentence
                            }) : null
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "grid min-w-[var(--component-agenda-min-width)] grid-cols-5 divide-x divide-border",
                    children: agenda.map((d)=>{
                        const shown = d.items.slice(0, PER_DAY);
                        const more = d.items.length - shown.length;
                        const isToday = d.day === today;
                        return /*#__PURE__*/ _jsxs("div", {
                            className: isToday ? 'bg-brand-muted/30 p-[var(--space-2-5)]' : 'p-[var(--space-2-5)]',
                            "data-day": d.day,
                            "data-testid": Testids.dashboard.agendaDay,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mb-[var(--space-2)] flex items-baseline justify-between gap-[var(--space-1)]",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            className: "font-medium",
                                            size: "body-ui",
                                            tone: isToday ? 'info' : 'primary',
                                            children: isToday ? 'Today' : weekday(d.day)
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            size: "meta",
                                            tone: "muted",
                                            children: cmDateShort(d.day)
                                        })
                                    ]
                                }),
                                loading ? null : shown.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    children: "Nothing due"
                                }) : /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-[var(--space-1-5)]",
                                    children: [
                                        shown.map((it)=>/*#__PURE__*/ _jsxs("div", {
                                                className: it.atRisk ? 'rounded-[var(--radius-control)] border border-l-2 border-border border-l-warning px-[var(--space-2)] py-[var(--space-1-5)]' : 'rounded-[var(--radius-control)] border border-border px-[var(--space-2)] py-[var(--space-1-5)]',
                                                "data-testid": Testids.dashboard.agendaItem,
                                                title: it.what,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SpecraText, {
                                                        as: "div",
                                                        className: "line-clamp-2",
                                                        size: "body-ui",
                                                        children: it.what
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraText, {
                                                        as: "div",
                                                        className: "truncate",
                                                        size: "meta",
                                                        tone: "muted",
                                                        children: it.sub
                                                    })
                                                ]
                                            }, it.key)),
                                        more > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                                            size: "meta",
                                            tone: "info",
                                            children: [
                                                "+",
                                                more.toLocaleString(),
                                                " more"
                                            ]
                                        }) : null
                                    ]
                                })
                            ]
                        }, d.day);
                    })
                })
            })
        ]
    });
}
